import memoize from 'lodash/memoize';
import moment from 'moment';
import qs from 'qs';
import { CONNECTION_TYPES } from '@consts';

import {
    toDate,
    isValueExist,
    isValidAppStoreUrl,
    isValidPlayStoreUrl,
    arrayToObject,
} from '@util';
import {
    fillAd,
    extractAppUrls,
    adjustDuplicate,
    cutAdCreative,
    isGoogleSearch_V1,
} from '@/utils/adsUtils';

var getData = R.prop('data');

const baseURL = '/campaigns';

const { GA } = CONNECTION_TYPES;

async function query(params) {
    const res = await axios.get(`${baseURL}`, {
        params,
        paramsSerializer: (params) =>
            qs.stringify(params, {
                arrayforamt: 'brackets',
            }),
    });
    return res.data;
}

// if data fields are not filled before
// setting to the store
// date will not be reactive
function fillData(data) {
    // schedule
    if (data.proposal) {
        data.proposal = fillProposal(data);
    } else if (data.onboarding) {
        //data.onboarding = fillOnboarding(data);
    }

    return data;
}

function fillProposal(campaign) {
    const proposal = campaign.proposal;
    var notValid = R.either(R.isNil, isNaN);
    if (!proposal.schedule) {
        proposal.schedule = {};
    }

    var daily = proposal.budget.daily;
    var lifeTime = proposal.budget.lifeTime;

    if (notValid(daily)) {
        proposal.budget.daily = 0;
    }

    if (notValid(lifeTime)) {
        proposal.budget.lifeTime = 0;
    }

    (proposal.budget.channels || []).forEach((b) => {
        if (notValid(b.value)) {
            b.value = 0;
        }
        if (notValid(b.lifeTimeValue)) {
            b.lifeTimeValue = 0; //min budget for lifeitme
        }
    });

    proposal.channelAds = R.compose(
        adjustDuplicate,
        R.map(R.compose(cutAdCreative, fillAd)),
        R.propOr([], 'channelAds')
    )(proposal);

    if (!proposal.meta) {
        proposal.meta = {};
    }

    // convert old format to new format
    proposal.meta.generatedAds = R.compose(
        R.map(
            R.when(
                isGoogleSearch_V1,
                R.mergeDeepLeft({
                    creative: {
                        titles: [],
                        descriptions: [],
                    },
                })
            )
        ),
        R.pathOr([], ['meta', 'generatedAds'])
    )(proposal);

    const appUrls = extractAppUrls(proposal.channelAds) || [];
    proposal.meta = proposal.meta || {
        webOrApp: {
            type: isValueExist(appUrls) ? 'app' : 'web',
        },
    };

    const notExist = R.when(R.complement(isValueExist));
    const appUrlFind = R.find(R.__, appUrls);

    proposal.meta.webOrApp = R.evolve(
        {
            appStoreUrl: notExist(R.always(appUrlFind(isValidAppStoreUrl))),
            playStoreUrl: notExist(R.always(appUrlFind(isValidPlayStoreUrl))),
        },
        proposal.meta.webOrApp
    );

    const audienceMap = arrayToObject(R.prop('name'), proposal.audienceProfile);

    const audienceSchedule = audienceMap['schedule'];

    if (!isValueExist(audienceSchedule)) {
        proposal.audienceProfile.push({
            name: 'schedule',
            values: {
                allDayLong: true,
                startHour: 0,
                endHour: 0,
                days: [
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                ].map(
                    R.applySpec({
                        value: R.always(true),
                        day: R.identity,
                    })
                ),
            },
        });
    }

    const targetType = audienceMap['targetType'];

    if (!isValueExist(targetType)) {
        proposal.audienceProfile.push({
            name: 'targetType',
            values: [
                {
                    name: 'remarketing',
                    value: true,
                },
                {
                    name: 'lookalike',
                    value: true,
                },
            ],
        });
    }

    const relationship = audienceMap['relationshipStatus'];

    if (
        isValueExist(relationship) &&
        !R.find(R.propEq('name', 'engaged'), relationship.values)
    ) {
        relationship.values.push({
            name: 'engaged',
            value: true,
        });
        relationship.values = R.uniqBy(R.prop('name'), relationship.values);
    }

    const videoAdFormat = audienceMap['videoAdFormat'];

    if (!isValueExist(videoAdFormat)) {
        proposal.audienceProfile.push({
            name: 'videoAdFormat',
            values: [
                { name: 'adFormat', value: 'SKIPPABLE_IN_STREAM' },
                { name: 'bidStrategy', value: 'TARGET_CPM' },
            ],
        });
    }

    var schedule = proposal.schedule;
    if (campaign.status === 'in_progress') {
        let current = moment();
        // const weekDay = current.weekday();
        let nbDayToAdd = 1;
        // if (weekDay > 4) {
        //     nbDayToAdd = 1;
        // }
        const minStartDate = current.startOf('day').add(nbDayToAdd, 'day');
        if (
            !schedule.startDate ||
            moment(schedule.startDate).isBefore(minStartDate)
        ) {
            schedule.startDate = minStartDate.add(1, 'hour').toDate();
        }

        let minEndDate = moment(schedule.asap ? new Date() : schedule.startDate)
            .startOf('day')
            .add(1, 'day')
            .toDate();

        if (
            !schedule.endDate ||
            moment(schedule.endDate).isBefore(minEndDate)
        ) {
            schedule.endDate = moment(minEndDate).endOf('day').toDate();
        }
    }

    if (schedule.asap === undefined) {
        schedule.asap = true;
    }

    return proposal;
}

function get(id) {
    return axios
        .get(`${baseURL}/${id}`)
        .then(getData)
        .then(fillData)
        .catch((err) => {
            console.log(err);
        });
}

async function copy(id) {
    const res = await axios.post(`${baseURL}/${id}/copy`);
    return res.data;
}

async function create(answers) {
    const res = await axios.post(baseURL, {
        chat_answers: answers,
    });
    return res.data;
}

async function createEmpty(value = {}) {
    const res = await axios.post(`${baseURL}/empty`, {
        data: value,
    });
    return res.data;
}

function update(id, data, query) {
    var url = R.join('/', [baseURL, id]);
    if (query !== undefined) {
        url += `?${query}`;
    }
    return axios
        .put(url, R.omit(['id', 'imageURL'], data))
        .then(getData)
        .then(fillProposal)
        .then((data) => {
            var schedule = data.schedule;

            if (schedule) {
                if (schedule.startDate) {
                    schedule.startDate = toDate(schedule.startDate);
                }
                if (schedule.endDate) {
                    schedule.endDate = toDate(schedule.endDate);
                }
            }
            return data;
        });
}

function deactivate(id) {
    return axios.post(`${baseURL}/${id}/deactivate`).then(getData);
}

async function getEstimations(id, proposal) {
    const res = await axios.post(`${baseURL}/${id}/estimate`, {
        proposal,
    });
    return res.data;
}

async function generateCreativeImages(id, channel, creative) {
    const res = await axios.post(`${baseURL}/${id}/creatives/generate`, {
        channel,
        creative,
    });
    return res.data;
}

function getFacebookProfile(id) {
    return axios
        .get(`${baseURL}/${id}/facebookPage`)
        .then(getData)
        .then((profile) => profile || undefined); //empty string edge case
}

//the preview of ads
//will not change immediately
var getFacebookPreview = memoize(
    (campaignId, adId, position) => {
        if (adId < 0) return new Promise((resolve) => resolve());
        var url = `${baseURL}/${campaignId}/previewFacebook/${adId}`;
        if (position !== undefined) {
            url += `?position=${position}`;
        }

        return axios.get(url).then(getData);
    },
    (...args) => args.join('-')
);

function execute(id, data = {}) {
    return axios.post(`${baseURL}/${id}/execute`, data);
}

function updateFacebook(campaignId, facebookPageId, instagram) {
    return axios
        .put(`${baseURL}/${campaignId}/updateAccounts`, {
            facebookPageId,
            instagram,
        })
        .then((res) => console.log(res))
        .catch((err) => console.log(err));
}

function updateGa(campaignId, googleAnalytics) {
    return axios
        .put(`${baseURL}/${campaignId}/updateAccounts`, {
            googleAnalytics,
        })
        .then((res) => console.log(res))
        .catch((err) => console.log(err));
}

function updatePixel(campaignId, facebookPixel, facebookUserToken) {
    return axios.put(`${baseURL}/${campaignId}/updateAccounts`, {
        facebookPixel,
        facebookUserToken,
    });
}

function updateFbApp(campaignId, fbApp) {
    return axios.put(`${baseURL}/${campaignId}/updateAccounts`, {
        fbApp,
    });
}

function updateYouTubeChannel(campaignId, youtubeChannel) {
    return axios.put(`${baseURL}/${campaignId}/updateAccounts`, {
        youtubeChannel,
    });
}

function updateTitle(id, title) {
    return axios
        .put(`${baseURL}/${id}/title`, {
            title,
        })
        .then(getData);
}

function deleteCampaign(ids) {
    return axios
        .delete(`${baseURL}`, {
            data: { ids },
        })
        .then(getData);
}

function debounceExecute(id) {
    return axios.post(`${baseURL}/${id}/debounceExecute`);
}

function bindCoupons(id, couponIds) {
    return axios.post(`${baseURL}/${id}/coupons`, {
        couponIds,
    });
}

function coupons(id, couponIds) {
    return axios.get(`${baseURL}/${id}/coupons`).then(R.prop('data'));
}

function availableCouponAmount(id) {
    return axios
        .get(`${baseURL}/${id}/availableCouponAmount`)
        .then(R.prop('data'));
}

function listConversions({ campaignId, type = GA }) {
    return axios
        .get(`${baseURL}/${campaignId}/conversions`, {
            params: {
                type,
            },
        })
        .then(R.prop('data'));
}

function addConversion(id, conversion) {
    return axios.post(`${baseURL}/${id}/conversions/select`, {
        conversion,
    });
}

async function nbLaunchedCampaigns() {
    const res = await axios.get(`${baseURL}/nbLaunchedCampaigns`);
    return R.path(['data', 'nb'], res);
}

function extend(id, data) {
    if (!isValueExist(data)) {
        return Promise.resolve();
    }
    return axios
        .put(`${baseURL}/${id}/extend`, R.pick(['budget', 'days'], data))
        .then(R.prop('data'));
}

function downloadExcel(id) {
    return axios.get(`${baseURL}/${id}/reports/excel`).then(getData);
}

function downloadCampaignsExcel() {
    return axios
        .get(`${baseURL}/excel`, {
            responseType: 'blob',
        })
        .then(getData);
}

function getOnBoardingCampaign() {
    return axios.get(`${baseURL}/onboarding`).then(getData);
}

function updateOnBoarding(id, onboarding) {
    return axios
        .post(`${baseURL}/${id}/onboarding`, {
            onboarding,
        })
        .then(getData);
}

function startOnBoardingCampaign(id) {
    return axios.post(`${baseURL}/${id}/startOnBoardingCampaign`).then(getData);
}

function checkAppstoreConnected(campaignId, appleAppId) {
    return axios.put(`${baseURL}/${campaignId}/updateAccounts`, {
        appleAppId,
    });
}

function allTimeMetrics(campaignId) {
    return axios.get(`${baseURL}/${campaignId}/metrics`).then(getData);
}

function searchByTitle(query = {}) {
    return axios.get(`${baseURL}?${qs.stringify(query)}`).then(R.prop('data'));
}

function checkSalesChatDisplayFlag() {
    return axios.get(`/supports/show_chatbox`).then(getData);
}

function sendSalesChatQuery(params) {
    return axios.post(`/supports`, params).then(getData);
}

const getPixelTestResult = (campaignId, pixelId, channel) => {
    return axios.get(`${baseURL}/${campaignId}/pixel/${pixelId}?channel=${channel}`).then(getData);
};

export default {
    query,
    copy,
    create,
    createEmpty,
    update,
    deactivate,
    get,
    getEstimations,
    getFacebookPreview,
    getFacebookProfile,
    execute,
    updateFacebook,
    updateTitle,
    deleteCampaign,
    updateGa,
    generateCreativeImages,
    debounceExecute,
    coupons,
    bindCoupons,
    availableCouponAmount,
    updatePixel,
    listConversions,
    addConversion,
    nbLaunchedCampaigns,
    extend,
    downloadExcel,
    getOnBoardingCampaign,
    updateOnBoarding,
    startOnBoardingCampaign,
    updateFbApp,
    downloadCampaignsExcel,
    checkAppstoreConnected,
    allTimeMetrics,
    updateYouTubeChannel,
    searchByTitle,
    checkSalesChatDisplayFlag,
    sendSalesChatQuery,
    getPixelTestResult,
};
